import { createSlice } from '@reduxjs/toolkit';

import { DEFAULT_TENANT } from '@/constants/tenant';
import { getTenantConfigs } from '@/constants/tenant-configs';
import { surinameDutchConfigs } from '@/constants/tenant-configs/suriname-dutch';

import {
  AppConfigurations,
  Feature,
  StoreAppConfigurations,
} from '@/types/app-configurations';
import { Tenant } from '@/types/common';
import { Option } from '@/types/components/common';
import { Language } from '@/types/i18n';

import {
  fetchBusinessCategories,
  fetchDirectorOccupations,
  fetchFeatures,
  fetchOccupations,
  setTenant,
} from './actions';

export type ConfigsDataState = {
  tenant: Tenant;
  features: Feature[];
  occupations: Option[];
  directorOccupations: Option[];
  businessCategories: Option[];
  businessCategoriesById: Record<string, Option>;
  initialized: {
    features: boolean;
    occupations: boolean;
    directorOccupations: boolean;
    businessCategories: boolean;
  };
} & StoreAppConfigurations;

const INITIAL_CONFIG_STATE: ConfigsDataState = {
  tenant: DEFAULT_TENANT,
  ...pickNonFunctionConfigs(getTenantConfigs(DEFAULT_TENANT)),
  features: [],
  occupations: [],
  directorOccupations: [],
  businessCategories: [],
  businessCategoriesById: {},
  initialized: {
    features: false,
    occupations: false,
    directorOccupations: false,
    businessCategories: false,
  },
};

export const configsSlice = createSlice({
  name: 'configs',
  initialState: INITIAL_CONFIG_STATE,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(setTenant, (state, { payload }) => {
      const { tenant, language } = payload;
      let targetConfigs = getTenantConfigs(tenant);

      if (tenant === Tenant.Suriname && language === Language.Dutch) {
        targetConfigs = surinameDutchConfigs;
      }

      Object.assign(state, {
        tenant,
        ...pickNonFunctionConfigs(targetConfigs),
        addressCityOptions: targetConfigs.addressCityOptions,
      });
    });
    builder.addCase(fetchFeatures.fulfilled, (state, { payload }) => {
      state.features = payload;
    });
    builder.addCase(fetchOccupations.fulfilled, (state, { payload }) => {
      state.occupations = payload;
    });

    builder.addCase(
      fetchDirectorOccupations.fulfilled,
      (state, { payload }) => {
        state.directorOccupations = payload;
      },
    );
    builder.addCase(fetchBusinessCategories.fulfilled, (state, { payload }) => {
      state.businessCategories = payload;
      state.businessCategoriesById = payload.reduce(
        (acc, category) => {
          acc[category.value] = category;
          return acc;
        },
        {} as Record<string, Option>,
      );
    });
    builder.addMatcher(fetchFeatures.settled, state => {
      state.initialized.features = true;
    });
    builder.addMatcher(fetchOccupations.settled, state => {
      state.initialized.occupations = true;
    });
    builder.addMatcher(fetchDirectorOccupations.settled, state => {
      state.initialized.directorOccupations = true;
    });
    builder.addMatcher(fetchBusinessCategories.settled, state => {
      state.initialized.businessCategories = true;
    });
  },
});

export const configsReducer = configsSlice.reducer;

function pickNonFunctionConfigs(
  configs: AppConfigurations,
): StoreAppConfigurations {
  if (!configs) return {} as StoreAppConfigurations;

  return Object.fromEntries(
    Object.entries(configs).filter(([, value]) => typeof value !== 'function'),
  ) as StoreAppConfigurations;
}
